import React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import OrderPanel from "./OrderPanel";
import { formatMoney } from "./billingMath";

export default function OrderRow({ batch, expanded, detail, loadingDetail, onToggle, panelProps }) {
  const total = Number(batch.total_amount || 0);
  const invoiced = Number(batch.invoiced_cash_amount ?? batch.paid_amount ?? 0);
  const planned = Number(batch.planned_cash_amount || 0);
  const linked = Math.min(total, invoiced + planned);
  const remaining = Math.max(total - linked, 0);
  const invoicedProgress = total > 0 ? Math.min(100, Math.round((invoiced / total) * 100)) : 0;
  const plannedProgress = total > 0 ? Math.min(100 - invoicedProgress, Math.round((planned / total) * 100)) : 0;

  return (
    <div
      className={`overflow-hidden rounded-xl border bg-white shadow-sm transition ${
        expanded ? "border-blue-300 ring-1 ring-blue-200" : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-3 py-3 text-left sm:px-4"
      >
        {expanded ? (
          <ChevronDown className="h-5 w-5 shrink-0 text-blue-600" />
        ) : (
          <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold text-slate-950">
              {batch.reference || `Commande ${batch.id}`}
            </span>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-500">
              {batch.line_count || 0} lignes
            </span>
          </div>
          <div className="truncate text-xs text-slate-500">{batch.name || batch.client_name || "Sans objet"}</div>
        </div>

        <div className="hidden shrink-0 items-center gap-6 sm:flex">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Commande</div>
            <div className="text-sm font-semibold text-slate-950">{formatMoney(total)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Facturé</div>
            <div className="text-sm font-semibold text-emerald-700">{formatMoney(invoiced)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Prévu</div>
            <div className="text-sm font-semibold text-amber-700">{formatMoney(planned)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Restant</div>
            <div className={`text-sm font-semibold ${remaining <= 0.01 ? "text-emerald-700" : "text-slate-950"}`}>
              {formatMoney(remaining)}
            </div>
          </div>
          <div className="w-28">
            <div className="mb-1 flex justify-between text-[10px] text-slate-400">
              <span>{invoicedProgress}%</span>
              <span>{formatMoney(remaining)}</span>
            </div>
            <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-1.5 bg-emerald-500" style={{ width: `${invoicedProgress}%` }} />
              <div className="h-1.5 bg-amber-400" style={{ width: `${plannedProgress}%` }} />
            </div>
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-3 text-right sm:hidden">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Commande</div>
            <div className="text-sm font-semibold text-slate-950">{formatMoney(total)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Restant</div>
            <div className={`text-sm font-semibold ${remaining <= 0.01 ? "text-emerald-700" : "text-slate-950"}`}>
              {formatMoney(remaining)}
            </div>
          </div>
        </div>
      </button>

      {expanded &&
        (loadingDetail || !detail ? (
          <div className="border-t border-slate-100 px-4 py-8 text-center text-sm text-slate-500">Chargement…</div>
        ) : (
          <OrderPanel batch={detail} {...panelProps} />
        ))}
    </div>
  );
}
